import React from 'react';
import { Box } from '@mui/material';
import { SharedAlgoliaProvider } from '../Algolia/SharedAlgoliaProvider';
import { ContentFilterProvider } from '../ContentFilters/ContentFilterProvider';
import ContentFilters, {
  ContentFiltersProps,
} from '../ContentFilters/components/ContentFilters';
import AlgoliaTable, { AlgoliaIndexTableProps } from './Table';

interface AlgoliaSearchWithFiltersProps {
  contentFiltersProps?: Omit<ContentFiltersProps, 'enableCounts'>;
  algoliaTableProps: AlgoliaIndexTableProps;
  indexName: string;
  enableCounts?: boolean;
  customContentFilters?: React.ComponentType<ContentFiltersProps>;
  layout?: {
    filtersPosition?: 'top' | 'left' | 'right';
    spacing?: number;
  };
}

/**
 * Unified component that provides both ContentFilters and AlgoliaTable
 * with shared Algolia context for count display and search
 */
export const AlgoliaSearchWithFilters: React.FC<AlgoliaSearchWithFiltersProps> = ({
  contentFiltersProps = {},
  algoliaTableProps,
  indexName,
  enableCounts = false,
  customContentFilters,
  layout = { filtersPosition: 'top', spacing: 16 },
}) => {
  const ContentFiltersComponent = customContentFilters || ContentFilters;

  const content = (
    <ContentFilterProvider>
      <Box
        sx={{
          display: 'flex',
          flexDirection: layout.filtersPosition === 'top' ? 'column' : 'row',
          gap: `${layout.spacing}px`,
          width: '100%',
          height: '100%',
        }}
      >
        {/* Content Filters */}
        <Box
          sx={{
            flexShrink: 0,
            ...(layout.filtersPosition === 'left' && { width: '300px' }),
            ...(layout.filtersPosition === 'right' && { width: '300px', order: 2 }),
          }}
        >
          <ContentFiltersComponent {...contentFiltersProps} enableCounts={enableCounts} />
        </Box>

        {/* Algolia Table */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0, // Important for flex containers with overflow
            ...(layout.filtersPosition === 'right' && { order: 1 }),
          }}
        >
          <AlgoliaTable {...algoliaTableProps} />
        </Box>
      </Box>
    </ContentFilterProvider>
  );

  // Always wrap with SharedAlgoliaProvider for unified context
  return <SharedAlgoliaProvider indexName={indexName}>{content}</SharedAlgoliaProvider>;
};

export default AlgoliaSearchWithFilters;
